import { SVGProps } from 'react';
import type { Request } from 'express';
import upperFirst from 'lodash-es/upperFirst';
import moment from 'moment';

import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import {
  type SearchParamsPetCounts,
  SearchParamsPetType,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
// eslint-disable-next-line rover/no-namespace-import-icons
import * as icons from '@rover/icons';
import type { ApiPersonDetailReviewsListParams } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListParams';
import type { ApiPersonPublicCalendarRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiPersonPublicCalendarRetrieveParams';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { HttpError, NetworkError } from '@rover/rsdk/src/errors';
import { camelizeString } from '@rover/rsdk/src/modules/Network/object-utilities';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { PetType } from '@rover/types/src/Pet';
import { RoverBrand, RoverBrandPetType } from '@rover/types/src/RoverBrand';
import { groupBy } from '@rover/utilities/array';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';
import {
  isCatOnlyAvailablePetTypesBasedOnPetTypes,
  isCatOnlyServiceBasedOnAcceptedPetTypes,
} from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import getRoverBrand from '@rover/utilities/getRoverBrand';

import { SitterProfileVersionOptions, SKILLS_ICONS_ADAPTER } from '../constants';
import { SitterProfileVersion } from '../hooks/useRevampProfileContext';

const DF = getDateTimeFormatMapForLang(DEFAULT_LANG);

type LocationParts = {
  neighborhood?: string;
  city?: string;
  state?: string;
};

export const getLocationString = (
  parts: LocationParts | undefined,
  zip?: string
): string | null => {
  if (!parts) return null;
  const { neighborhood, city, state } = parts;
  // Drop the neighborhood when it case-insensitively matches the city.
  const dedupedNeighborhood =
    neighborhood && neighborhood.toLowerCase() !== city?.toLowerCase() ? neighborhood : undefined;
  return [dedupedNeighborhood, city, state, zip].filter(Boolean).join(', ') || null;
};

export const getPublicCalendarParams = (req: Request): ApiPersonPublicCalendarRetrieveParams => {
  const selectedDate = moment(req.params.start_date) || moment();
  const { serviceType } = req.params;
  const startDate = selectedDate.clone().startOf('month').subtract(7, 'day').format(DF.DATE_ISO);
  const endDate = selectedDate.clone().endOf('month').add(7, 'day').format(DF.DATE_ISO);

  return {
    startDate,
    endDate,
    serviceType,
  };
};

export const getOpkFromReviewUrl = (reviewUrl: string): string => {
  return (
    reviewUrl
      .substring(0, reviewUrl.length - 1)
      .split('/')
      .pop() || ''
  );
};

export const renderIcon = (
  iconName: string | undefined,
  returnDefault: boolean = true
): React.ComponentType<SVGProps<SVGElement>> | undefined => {
  if (iconName && icons[upperFirst(camelizeString(iconName))]) {
    return icons[upperFirst(camelizeString(iconName))];
  }
  if (returnDefault) return icons.AlertInfo;
  return undefined;
};

/* This method is used to know if the error is a timeout by message content */
export const isAxiosTimeoutError = (error: Error): boolean => {
  return error.message?.match(/.*axiosUtils timed out.*/) !== null;
};

export const isAxiosUntaggedError = (error: Error): boolean => {
  return error.message?.match(/.*parseAxiosError called with a non-axios error.*/) !== null;
};

export const isAxiosError = (error: Error): boolean => {
  return (
    error instanceof HttpError ||
    error instanceof NetworkError ||
    (error instanceof Error && (isAxiosTimeoutError(error) || isAxiosUntaggedError(error)))
  );
};

/**
 * Determines the primary pet type for both banner display and rate selection based on selected pets.
 * There is no distinction in logic for price transparency levels or service type.
 * If a pet is selected that is not part of the brand's available pets, it will be ignored.
 *
 * If the brand is cat-only or all sitter services are cat-only (based on sitter pet preferences),
 * always returns CAT regardless of pet selection.
 * If no pets are selected and the sitter's services are all cat-only, defaults to CAT.
 * If no pets are selected otherwise, defaults to DOG.
 * Otherwise, prioritizes PUPPY, then DOG, then CAT.
 */
export const usePrimaryPetType = (
  petCounts: SearchParamsPetCounts,
  selectedPetTypesString?: SearchParamsPetType,
  services?: EnhancedPersonSitterProfileService[]
): PetType => {
  const brandPets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandPets);

  // If all sitter services only accept cats (based on sitter pet preferences), return CAT
  // regardless of pet selection query params
  if (services?.length && services.every(isCatOnlyServiceBasedOnAcceptedPetTypes)) {
    return PetType.CAT;
  }

  const selectedPetTypes = selectedPetTypesString?.split(',') ?? [];

  const isCatSelected = petCounts.selectedCatCount > 0 || selectedPetTypes.includes(PetType.CAT);
  const isDogSelected = petCounts.selectedDogCount > 0 || selectedPetTypes.includes(PetType.DOG);
  const isPuppySelected = petCounts.selectedPuppyCount > 0;

  // Pet selection checks — cat-only brands skip PUPPY/DOG and fall through to CAT default
  if (isPuppySelected && brandPets.includes(PetType.PUPPY) && !isCatOnlyBrand) {
    return PetType.PUPPY;
  }
  if (isDogSelected && brandPets.includes(PetType.DOG) && !isCatOnlyBrand) {
    return PetType.DOG;
  }
  if (isCatSelected && brandPets.includes(PetType.CAT)) {
    return PetType.CAT;
  }

  // No pets selected — cat-only brands default to CAT, otherwise DOG
  return isCatOnlyBrand ? PetType.CAT : PetType.DOG;
};

export const getIconNameAdapted = (iconName: string | undefined): string | undefined => {
  if (iconName) {
    const sanitizedIconName = upperFirst(camelizeString(iconName));
    return SKILLS_ICONS_ADAPTER[sanitizedIconName] || iconName;
  }
  return iconName;
};

export const getYearsOfExperience = (sitter: EnhancedPersonSitterProfile): number =>
  sitter.providerProfile?.yearsOfExperience ?? 0;

/**
 * Whether to surface the years-of-experience highlight in place of the ratings summary
 * (profile header, mobile header, gallery widget): only for sitters with no reviews yet
 * who have at least one recorded year of experience.
 */
export const showExperienceHighlight = (sitter: EnhancedPersonSitterProfile): boolean =>
  sitter.reviewsCount === 0 && getYearsOfExperience(sitter) >= 1;

export const getInitialReviewsParams = (): ApiPersonDetailReviewsListParams => {
  const brand = getRoverBrand();
  const brandPets = RoverBrandPetType[brand] ?? RoverBrandPetType[RoverBrand.Rover];
  const isCatOnly = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandPets);
  return { page: 1, ...(isCatOnly && { petType: 'cat' as const }) };
};

export const shouldShowRevampLayout = (
  sitterInfo: EnhancedPersonSitterProfile | undefined
): boolean => {
  if (!sitterInfo?.sitterProfileVersion) return false;
  return sitterInfo.sitterProfileVersion === SitterProfileVersionOptions.REVAMP;
};

export const isLegacyVersion = (input: SitterProfileVersion): boolean => {
  return input === SitterProfileVersionOptions.LEGACY;
};

export const isHostedServiceType = (slug: string | undefined): boolean =>
  slug === ServiceType.OVERNIGHT_BOARDING || slug === ServiceType.DOGGY_DAY_CARE;

export const groupServicesByLocationType = (
  services: EnhancedPersonSitterProfileService[]
): {
  hostingActiveServices: EnhancedPersonSitterProfileService[];
  travelingActiveServices: EnhancedPersonSitterProfileService[];
} => {
  const { hostingActiveServices = [], travelingActiveServices = [] } = groupBy(
    services.filter((s) => !s.isAway),
    (service) =>
      isHostedServiceType(service.serviceType.slug)
        ? 'hostingActiveServices'
        : 'travelingActiveServices'
  );
  return { hostingActiveServices, travelingActiveServices };
};
